import * as React from 'react';

import { format, isAfter, isBefore, startOfDay } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardFooter } from '@/components/ui/card';

interface DateTimePickerProps {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  minDate?: Date;
}

export function DateTimePicker({ value, onChange, minDate = new Date() }: DateTimePickerProps) {
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(value);
  const [selectedTime, setSelectedTime] = React.useState<string | null>(
    value ? format(value, 'HH:mm') : null,
  );

  // Generate time slots in 30-minute intervals (12:00 AM to 11:30 PM)
  const generateTimeSlots = React.useMemo(() => {
    const slots: { display: string; value: string }[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

        // Convert to 12-hour format for display
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour >= 12 ? 'PM' : 'AM';
        const display = `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;

        slots.push({ display, value });
      }
    }
    return slots;
  }, []);

  // Filter time slots based on selected date and minDate
  const availableTimeSlots = React.useMemo(() => {
    if (!selectedDate) return generateTimeSlots;

    const selectedDateOnly = startOfDay(selectedDate);
    const minDateOnly = startOfDay(minDate);

    // If selected date is after minDate, all times are available
    if (isAfter(selectedDateOnly, minDateOnly)) {
      return generateTimeSlots;
    }

    // If selected date is today, filter out past times
    if (selectedDateOnly.getTime() === minDateOnly.getTime()) {
      const currentHour = minDate.getHours();
      const currentMinute = minDate.getMinutes();

      return generateTimeSlots.filter((slot) => {
        const [hour, minute] = slot.value.split(':').map(Number);
        return hour > currentHour || (hour === currentHour && minute > currentMinute);
      });
    }

    return generateTimeSlots;
  }, [selectedDate, minDate, generateTimeSlots]);

  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);

    // If we have both date and time, combine them and call onChange
    if (date && selectedTime) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const combined = new Date(date);
      combined.setHours(hours, minutes, 0, 0);

      // Validate it's after minDate
      if (isAfter(combined, minDate)) {
        onChange(combined);
      }
    }
  };

  // Handle time selection
  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);

    // If we have both date and time, combine them and call onChange
    if (selectedDate && time) {
      const [hours, minutes] = time.split(':').map(Number);
      const combined = new Date(selectedDate);
      combined.setHours(hours, minutes, 0, 0);

      // Validate it's after minDate
      if (isAfter(combined, minDate)) {
        onChange(combined);
      }
    }
  };

  // Get disabled dates (before minDate)
  const disabledDates = React.useCallback(
    (date: Date) => {
      return isBefore(startOfDay(date), startOfDay(minDate));
    },
    [minDate],
  );

  return (
    <Card className='gap-0 p-0 border-0 shadow-none'>
      <CardContent className='p-0'>
        <div className='grid grid-cols-1 md:grid-cols-[1fr,auto]'>
          {/* Calendar Section */}
          <div className='flex justify-center'>
            <Calendar
              mode='single'
              selected={selectedDate}
              onSelect={handleDateSelect}
              defaultMonth={selectedDate || minDate}
              disabled={disabledDates}
              showOutsideDays={false}
              className=''
              formatters={{
                formatWeekdayName: (date) => {
                  return date.toLocaleString('en-US', { weekday: 'short' });
                },
              }}
            />
          </div>

          {/* Time Slots Sidebar */}
          <div className='w-full md:w-48 border-t md:border-t-0 md:border-l'>
            <div className='h-full max-h-72 md:max-h-[400px] overflow-y-auto p-2'>
              <div className='grid gap-1.5'>
                {availableTimeSlots.length > 0 ? (
                  availableTimeSlots.map((slot) => (
                    <Button
                      key={slot.value}
                      variant={selectedTime === slot.value ? 'default' : 'outline'}
                      onClick={() => handleTimeSelect(slot.value)}
                      className='w-full shadow-none text-sm h-8'
                      type='button'
                    >
                      {slot.display}
                    </Button>
                  ))
                ) : (
                  <div className='text-center text-sm text-muted-foreground py-4'>
                    No available times for today
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>

      <CardFooter className='border-t px-3 py-2'>
        <div className='text-sm text-center w-full'>
          {selectedDate && selectedTime ? (
            <>
              Restart scheduled for{' '}
              <span className='font-medium'>
                {selectedDate?.toLocaleDateString('en-US', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>{' '}
              at{' '}
              <span className='font-medium'>
                {generateTimeSlots.find((slot) => slot.value === selectedTime)?.display ||
                  selectedTime}
              </span>
            </>
          ) : (
            <>Select a date and time for the restart</>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}
